import React, { useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon,
  Sparkles,
  Calendar,
  Star,
  StarOff,
  Pencil,
  Play,
  Pause,
  RotateCcw,
  Layers,
  Plus,
  Clock,
  User,
  Users,
  Shield,
  Film,
  UploadCloud,
  Trash2,
} from "lucide-react";
import {
  useBackgroundStore,
  upcomingSchedule,
  makeLayer,
} from "./BackgroundProvider";
import {
  putBlob,
  makeIdbSrc,
  compressForUpload,
  mediaSrcToUrl,
  MAX_UPLOAD_BYTES,
  UPLOAD_TARGET_BYTES,
} from "./mediaStore";
import BGMiniPreview from "./BGMiniPreview";
import BGEditDrawer from "./BGEditDrawer";
import SceneBuilder from "./SceneBuilder";
import { T } from "../../components/em/tokens";
import {
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  Chip,
  EmptyState,
} from "../../components/em/ui";
import { SettingsCard, Toggle } from "../../pages/Settings/settingsUi";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
const FILTERS = [
  { key: "all", label: "All" },
  { key: "scene", label: "Scenes", tag: null, kind: "scene" },
  { key: "video", label: "Video / GIF", tag: null, kind: "video" },
  { key: "nature", label: "Nature", tag: "nature" },
  { key: "office", label: "Office", tag: "office" },
  { key: "seasonal", label: "Seasonal", tag: "seasonal" },
  { key: "custom", label: "Custom", tag: "custom" },
];

const MAX_INLINE_BYTES = 5 * 1024 * 1024; // 5 MB — warning threshold

const scheduleChip = (schedule) => {
  const k = schedule?.kind || "library";
  if (k === "default")
    return { label: "Default", bg: "#dbeafe", color: "#1d4ed8" };
  if (k === "once")
    return { label: "One-time", bg: "#fef3c7", color: "#92400e" };
  if (k === "yearly")
    return { label: "Yearly", bg: "#fce7f3", color: "#be185d" };
  if (k === "timeOfDay")
    return { label: "Time-of-day", bg: "#dcfce7", color: "#166534" };
  return { label: "Library", bg: T.bgMuted, color: T.fg4 };
};

// Compact icon-only action button with a custom dark-pill tooltip on hover.
function IconBtn({
  icon: Icon,
  tip,
  onClick,
  active = false,
  tone = "default",
}) {
  const [hover, setHover] = useState(false);
  const palette = active
    ? { bg: T.orange50, border: T.orange, color: T.orangeDeep }
    : tone === "danger"
      ? { bg: "#fff", border: "#fecaca", color: T.danger }
      : { bg: "#fff", border: T.border, color: T.fg3 };
  return (
    <div
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={tip}
        style={{
          padding: 0,
          width: 32,
          height: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: T.rMd,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          color: palette.color,
          cursor: "pointer",
          transition: "background 120ms, border-color 120ms",
        }}
      >
        <Icon size={15} />
      </button>
      {hover && (
        <span
          role="tooltip"
          style={{
            padding: "0",
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: T.bgBlack,
            color: "#fff",
            font: `500 11px ${T.font}`,
            padding: "4px 8px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            zIndex: 50,
            pointerEvents: "none",
            boxShadow: "0 4px 12px -4px rgba(0,0,0,0.45)",
          }}
        >
          {tip}
        </span>
      )}
    </div>
  );
}

export default function BackgroundsSettings() {
  const {
    state,
    active,
    addBackground,
    updateBackground,
    setDefault,
    setEmployeeDefault,
    setParallaxEnabled,
    setRole,
    resetAll,
    deleteBackground,
  } = useBackgroundStore();

  const [filter, setFilter] = useState("all");
  const [editId, setEditId] = useState(null);
  const [sceneId, setSceneId] = useState(null);
  const [sceneNew, setSceneNew] = useState(false);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const editing = useMemo(
    () => state.backgrounds.find((b) => b.id === editId) || null,
    [editId, state.backgrounds],
  );
  const sceneEditing = useMemo(
    () => state.backgrounds.find((b) => b.id === sceneId) || null,
    [sceneId, state.backgrounds],
  );

  const filtered = useMemo(() => {
    if (filter === "all") return state.backgrounds;
    if (filter === "scene")
      return state.backgrounds.filter((b) => b.kind === "scene");
    if (filter === "video")
      return state.backgrounds.filter((b) => b.kind === "video");
    return state.backgrounds.filter((b) => (b.tags || []).includes(filter));
  }, [filter, state.backgrounds]);

  const upcoming = useMemo(
    () => upcomingSchedule(state, new Date(), 6),
    [state],
  );

  // Single-image static upload: no parallax, no 3-layer fan.
  // Per-image sliders (blur, scale, rotate, offsetX, offsetY) live on the
  // single layer and are edited in BGEditDrawer.
  const onUploadImage = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) return;
    // Re-encode before anything else. The picked file is what eventually gets
    // POSTed to /media, and a raw camera photo is many times the reverse
    // proxy's body limit — that mismatch is what surfaced as a 413 on the live
    // host. Compressing here fixes it for both the IndexedDB copy and the
    // upload, and keeps the two in sync.
    let blob;
    try {
      blob = await compressForUpload(file);
    } catch (e) {
      toast.error(e?.message || "Couldn't read that image.");
      return;
    }
    if (blob.size > MAX_INLINE_BYTES) {
      // Still large after compression: stash in IndexedDB for capacity.
      const key = "img-" + Math.random().toString(36).slice(2, 9);
      await putBlob(key, blob);
      const src = makeIdbSrc(key);
      addStaticBg(file.name, src, src);
    } else {
      const src = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(blob);
      });
      addStaticBg(file.name, src, src);
    }
  };

  const addStaticBg = (fileName, src, thumb) => {
    const id = "bg-" + Math.random().toString(36).slice(2, 9);
    addBackground({
      id,
      name: String(fileName).replace(/\.[^.]+$/, ""),
      tags: ["custom"],
      type: "image",
      src,
      thumb,
      kind: "static",
      layers: [
        makeLayer(src, {
          depth: 0,
          scale: 100,
          rotate: 0,
          blur: 0,
          offsetX: 0,
          offsetY: 0,
        }),
      ],
      priority: 20,
      schedule: { kind: "library" },
      settings: {
        blur: 0,
        dim: 25,
        parallax: 0,
        overlayColor: "#0a0a0a",
        overlayAlpha: 0,
        scale: 100,
      },
    });
  };

  // Video / GIF upload — no per-image settings, respects dim/overlay/schedule.
  const onUploadVideo = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) return;
    const isGif = file.type === "image/gif";
    const mediaType = isGif ? "gif" : "video";
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(
        `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB — over the ` +
          `${MAX_UPLOAD_BYTES / 1024 / 1024} MB limit. Please compress it first.`,
      );
      return;
    }
    // Video and animated GIF can't be re-encoded in the browser the way images
    // can, so anything past the upload budget will be refused by the server
    // (413). Say so up front instead of letting the save fail later.
    if (file.size > UPLOAD_TARGET_BYTES) {
      const proceed = await confirmDialog(
        `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. ` +
          `Files over ${Math.round(UPLOAD_TARGET_BYTES / 1024)} KB may be rejected by the server ` +
          "upload limit, and will only be available on this device if they are. Continue?",
      );
      if (!proceed) return;
    }
    const key =
      (isGif ? "gif-" : "vid-") + Math.random().toString(36).slice(2, 9);
    await putBlob(key, file);
    const src = makeIdbSrc(key);
    addVideoBg(file.name, src, src, mediaType);
  };

  const addVideoBg = (fileName, src, thumb, mediaType) => {
    const id = "bg-" + Math.random().toString(36).slice(2, 9);
    addBackground({
      id,
      name: String(fileName).replace(/\.[^.]+$/, ""),
      tags: ["custom", "video"],
      type: mediaType,
      mediaType,
      src,
      thumb,
      kind: "video",
      layers: [
        makeLayer(src, {
          depth: 0,
          scale: 100,
          rotate: 0,
          blur: 0,
          offsetX: 0,
          offsetY: 0,
        }),
      ],
      priority: 20,
      schedule: { kind: "library" },
      settings: {
        blur: 0,
        dim: 20,
        parallax: 0,
        overlayColor: "#0a0a0a",
        overlayAlpha: 0,
        scale: 100,
      },
    });
  };

  // Open the Scene Builder on a blank draft. The scene is only created (POSTed
  // to the server) when the user clicks Save inside the builder — so cancelling
  // leaves nothing behind, and there's no client/server id mismatch.
  const startNewScene = () => {
    setSceneId(null);
    setSceneNew(true);
  };

  // ── small reusable pieces ─────────────────────────────────────────────
  const roleToggle = (
    <div
      style={{
        display: "inline-flex",
        background: T.bgMuted,
        borderRadius: T.rLg,
        padding: 3,
      }}
    >
      {[
        { k: "admin", icon: <Shield size={13} />, label: "Admin view" },
        { k: "employee", icon: <User size={13} />, label: "Employee view" },
      ].map((r) => {
        const on = state.role === r.k;
        return (
          <button
            key={r.k}
            onClick={() => setRole(r.k)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 12px",
              borderRadius: T.rMd,
              border: 0,
              cursor: "pointer",
              background: on ? "#fff" : "transparent",
              color: on ? T.orange : T.fg4,
              font: `${on ? 600 : 500} 12px/1 ${T.font}`,
              boxShadow: on ? T.shadowSm : "none",
              transition: "all .15s",
            }}
          >
            {r.icon} {r.label}
          </button>
        );
      })}
    </div>
  );

  const parallaxPill = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px 6px 14px",
        borderRadius: T.pill,
        border: `1px solid ${T.border}`,
        background: "#fff",
        boxShadow: T.shadowXs,
      }}
    >
      {state.parallaxEnabled ? (
        <Play size={13} color={T.orange} />
      ) : (
        <Pause size={13} color={T.fg5} />
      )}
      <span style={{ font: `600 12px/1 ${T.font}`, color: T.fg2 }}>
        Parallax
      </span>
      <Toggle checked={state.parallaxEnabled} onChange={setParallaxEnabled} />
    </div>
  );

  const resetBtn = (
    <SecondaryButton
      onClick={resetAll}
      title="Restore the built-in library — clears hidden built-ins for everyone. Uploaded images and scenes are kept."
      style={{ color: T.fg4 }}
    >
      <RotateCcw size={14} /> Reset
    </SecondaryButton>
  );

  return (
    <div
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "0 32px 48px",
        font: T.font,
      }}
    >
      <PageHeader
        icon={ImageIcon}
        title="Dashboard Backgrounds"
        subtitle="Set a default, schedule seasonal or time-of-day backgrounds, and fine-tune depth."
        actions={
          <>
            {parallaxPill}
            {roleToggle}
            {resetBtn}
          </>
        }
      />

      {/* Now showing + Upcoming schedule */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <Card padding={0} style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "16px 18px",
              borderBottom: `1px solid ${T.divider}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: T.rMd,
                    background: T.orange50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Sparkles size={16} color={T.orange} />
                </div>
                <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                  Now showing
                </div>
              </div>
              <Chip bg="#ecfccb" color="#3f6212" border="1px solid #bef264">
                <Sparkles size={11} /> {active.reason}
              </Chip>
            </div>
            <div
              style={{
                font: `400 12px ${T.font}`,
                color: T.fg5,
                paddingLeft: 42,
              }}
            >
              Live preview of what{" "}
              {state.role === "admin" ? "admins" : "employees"} see right now
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <BGMiniPreview bg={active.bg} height={220} />
          </div>
        </Card>

        <Card padding={0}>
          <div
            style={{
              padding: "16px 18px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: T.rMd,
                background: T.orange50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Calendar size={16} color={T.orange} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                Upcoming schedule
              </div>
              <div
                style={{
                  font: `400 12px ${T.font}`,
                  color: T.fg5,
                  marginTop: 2,
                }}
              >
                Backgrounds automatically queued by date or time
              </div>
            </div>
          </div>
          <div style={{ padding: "8px 0", maxHeight: 240, overflowY: "auto" }}>
            {upcoming.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  font: `400 12px ${T.font}`,
                  color: T.fg7,
                }}
              >
                <Clock size={20} style={{ opacity: 0.4 }} />
                <div style={{ marginTop: 6 }}>
                  No scheduled backgrounds — try setting a holiday or
                  time-of-day one.
                </div>
              </div>
            )}
            {upcoming.map((it, i) => (
              <div
                key={i}
                onClick={() => setEditId(it.bg.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 18px",
                  cursor: "pointer",
                  borderBottom:
                    i === upcoming.length - 1 ? 0 : `1px solid ${T.divider}`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 36,
                    borderRadius: T.rSm,
                    background: T.bgMuted,
                    backgroundImage: it.bg.thumb
                      ? `url("${mediaSrcToUrl(it.bg.thumb)}")`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    flexShrink: 0,
                    border: `1px solid ${T.border}`,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      font: `600 12px ${T.font}`,
                      color: T.fg1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.bg.name}
                  </div>
                  <div style={{ font: `400 11px ${T.font}`, color: T.fg5 }}>
                    {it.kind === "timeOfDay"
                      ? `${it.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${it.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                      : `${it.start.toLocaleDateString()} – ${it.end.toLocaleDateString()}`}
                  </div>
                </div>
                <Chip
                  bg={scheduleChip(it.bg.schedule).bg}
                  color={scheduleChip(it.bg.schedule).color}
                >
                  P{it.bg.priority || 0}
                </Chip>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Defaults summary */}
      <SettingsCard
        icon={Star}
        title="Defaults"
        subtitle="What each audience sees when nothing is scheduled"
        bodyPadding="20px 24px"
        style={{ marginBottom: 20 }}
      >
        <div
          style={{ font: `400 13px ${T.font}`, color: T.fg4, lineHeight: 1.6 }}
        >
          Admin default:{" "}
          <b style={{ color: T.fg1 }}>
            {state.backgrounds.find((b) => b.id === state.defaultId)?.name ||
              "—"}
          </b>
          {state.employeeDefaultId && (
            <>
              {" "}
              · Employees see:{" "}
              <b style={{ color: T.fg1 }}>
                {state.backgrounds.find((b) => b.id === state.employeeDefaultId)
                  ?.name || "—"}
              </b>
            </>
          )}
        </div>
      </SettingsCard>

      {/* Library section */}
      <SettingsCard
        icon={Layers}
        title="Background library"
        subtitle="Upload media, build layered scenes, and pick defaults"
        bodyPadding="0"
        actions={
          <>
            <SecondaryButton onClick={() => imageRef.current?.click()}>
              <UploadCloud size={15} /> Upload image
            </SecondaryButton>
            <SecondaryButton onClick={() => videoRef.current?.click()}>
              <Film size={15} /> Upload video / GIF
            </SecondaryButton>
            <PrimaryButton onClick={startNewScene}>
              <Layers size={15} /> Create scene
            </PrimaryButton>
          </>
        }
      >
        <input
          ref={imageRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            onUploadImage(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={videoRef}
          type="file"
          accept="video/mp4,video/webm,image/gif"
          style={{ display: "none" }}
          onChange={(e) => {
            onUploadVideo(e.target.files);
            e.target.value = "";
          }}
        />

        {/* Filter pills */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            padding: "16px 24px 12px",
          }}
        >
          {FILTERS.map((f) => {
            const on = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: "7px 14px",
                  borderRadius: T.pill,
                  border: 0,
                  background: on ? T.fg1 : T.bgMuted,
                  color: on ? "#fff" : T.fg4,
                  font: `${on ? 600 : 500} 12px/1 ${T.font}`,
                  cursor: "pointer",
                  transition: "all .15s",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Cards grid */}
        <div style={{ padding: "0 24px 24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
              gap: 14,
            }}
          >
            {filtered.map((b) => {
              const sch = scheduleChip(b.schedule);
              const isDefault = state.defaultId === b.id;
              const isEmpDefault = state.employeeDefaultId === b.id;
              const isActive = active.bg?.id === b.id;
              return (
                <div
                  key={b.id}
                  style={{
                    borderRadius: T.rXl,
                    overflow: "hidden",
                    border: isActive
                      ? `2px solid ${T.orange}`
                      : `1px solid ${T.border}`,
                    background: "#fff",
                    boxShadow: T.shadowXs,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    onClick={() => setEditId(b.id)}
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    <BGMiniPreview
                      bg={b}
                      height={140}
                      interactive={false}
                      showLabel={false}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        display: "flex",
                        gap: 4,
                      }}
                    >
                      {isDefault && (
                        <Chip
                          bg={T.fg1}
                          color="#fff"
                          style={{ padding: "3px 8px" }}
                        >
                          <Star size={10} /> DEFAULT
                        </Chip>
                      )}
                      {isActive && (
                        <Chip
                          bg={T.orange}
                          color="#fff"
                          style={{ padding: "3px 8px" }}
                        >
                          <Sparkles size={10} /> LIVE
                        </Chip>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          font: `600 13px ${T.font}`,
                          color: T.fg1,
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {b.name}
                      </div>
                      {b.kind === "scene" && (
                        <Chip
                          bg={T.orange50}
                          color={T.orangeDeep}
                          border={`1px solid ${T.orange100}`}
                        >
                          <Layers size={10} /> {b.layers?.length || 0}
                        </Chip>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap",
                        marginBottom: 10,
                      }}
                    >
                      <Chip bg={sch.bg} color={sch.color}>
                        {sch.label}
                      </Chip>
                      {isEmpDefault && (
                        <Chip bg="#cffafe" color="#0e7490">
                          <Star size={10} /> Employee default
                        </Chip>
                      )}
                    </div>
                    <div
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                    >
                      <IconBtn
                        icon={isDefault ? StarOff : Star}
                        active={isDefault}
                        tip={
                          isDefault
                            ? "Default — click to unset"
                            : "Set as default"
                        }
                        onClick={() =>
                          isDefault ? setDefault(null) : setDefault(b.id)
                        }
                      />
                      {/* HR is admin-tier — same background controls as Admin. */}
                      {(state.role === "admin" || state.role === "hr") && (
                        <IconBtn
                          icon={Users}
                          active={isEmpDefault}
                          tip={
                            isEmpDefault
                              ? "Employee default — click to clear"
                              : "Set as employee default"
                          }
                          onClick={() =>
                            isEmpDefault
                              ? setEmployeeDefault(null)
                              : setEmployeeDefault(b.id)
                          }
                        />
                      )}
                      <div style={{ flex: 1 }} />
                      <IconBtn
                        icon={Pencil}
                        tip="Edit"
                        onClick={() => setEditId(b.id)}
                      />
                      <IconBtn
                        icon={Trash2}
                        tone="danger"
                        tip="Delete"
                        onClick={async () => {
                          if (await confirmDialog(`Delete "${b.name}"?`)) {
                            deleteBackground(b.id);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <EmptyState
              icon={ImageIcon}
              title="No backgrounds in this filter yet"
              subtitle="Upload an image or video, add one from a URL, or create a layered scene."
              action={
                <PrimaryButton onClick={startNewScene}>
                  <Layers size={15} /> Create scene
                </PrimaryButton>
              }
            />
          )}
        </div>
      </SettingsCard>

      {editing && (
        <BGEditDrawer
          bg={editing}
          onClose={() => setEditId(null)}
          onEditScene={(id) => {
            setEditId(null);
            setSceneId(id);
            setSceneNew(false);
          }}
        />
      )}
      {(sceneEditing || sceneNew) && (
        <SceneBuilder
          existing={sceneEditing}
          onClose={() => {
            setSceneId(null);
            setSceneNew(false);
          }}
        />
      )}
    </div>
  );
}
